// Package imports:
import React, { useRef, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Form, Formik } from 'formik';
// Component imports:
import Button from '../../../ui-elements/Button/Button';
import Input from '../../../ui-elements/Input/Formik/Input';
import Loading from '../../../ui-elements/Loading/Loading';
import Link from '../../../ui-elements/Link/Link';
import HeadSection from '../../../ui-elements/HeadSection/HeadSection';
import DisplayBox from '../../DisplayBox/DisplayBox';
import Table from '../../../ui-elements/Table/Table';
import HistoryButtonDropDown from '../../HistoryButtonDropDown/HistoryButtonDropDown';
import Tooltip from '../../../ui-elements/Tooltip/Tooltip';
// Service imports:
import { isSSN } from '../../../services/utils';
import { useBuyingProcess } from '../../../services/buyhook';
import { GET_KELDAN_API_URL } from '../../../services/config';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { IndividualSearch, IndividualSearchResult } from '../../../types/IndividualTypes';
import { IKeldanApiResponse } from '../../../types/KeldanTypes';

interface IProps {
    isAuthenticated: boolean
}

const Thjodskra: React.FC<IProps> = ({ isAuthenticated }) => {
    const [ searchResults, setSearchResults ] = useState<IndividualSearchResult[]>();
    const [ searchButtonloading, setSearchButtonloading ] = useState(false);
    const [ searchTermResultName, setSearchTermResultName ] = useState('');

    const searchResultRef = useRef<HTMLDivElement | null>(null)

    const { tryPurchaseItem, isItemLoading, setToastError, resetState, ModalsAndToasts } = useBuyingProcess(async (ssn: string, modalReference: string) => {
        try {
            const requestQuery = new URLSearchParams({
                id: ssn, 
                reference: modalReference
            })
            const url = `${GET_KELDAN_API_URL()}/Individual/BuyProfile?${requestQuery.toString()}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                }
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                const responseBody: IKeldanApiResponse<IndividualSearch> = await response.json();
                const { message } = responseBody;
                if (message === ErrorMessages.NoCard) {
                    resetState('no card');
                } else if (message) resetState({
                    type: 'alert',
                    headText: message
                });
                else resetState('bad request');
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            resetState('network error');
        }
    }, 'þjóðskrá')

    const handleSearch = async (ssn: string) => {
        setSearchButtonloading(true);
        try {
            const requestQuery = new URLSearchParams({
                term: ssn
            })
            const url = `${GET_KELDAN_API_URL()}/Individual/Search`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                const responseBody: IKeldanApiResponse<IndividualSearch> = await response.json();
                if (responseBody.success) {
                    if (responseBody.result){
                        setSearchResults(responseBody.result.list.list.list);
                        setSearchTermResultName(ssn);
                        searchResultRef.current?.scrollIntoView();
                    }
                } else {
                    const { message } = responseBody;
                    if (message === ErrorMessages.NoCard) {
                        setToastError('no card');
                    } else if (message) {
                        setToastError({
                            type: 'alert',
                            headText: message
                        });
                    } else {
                        setToastError('bad request');
                    }
                }
            } else {
                setToastError('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            setToastError('network error');
        }
        setSearchButtonloading(false);
    }
    
    const displayForm = (type: 'disabled' | 'enabled') => (
        <Formik
            initialValues={{
                ssn: ''
            }}
            validationSchema={Yup.object({
                ssn: Yup.string().required('Leit má ekki vera tóm')
            })}
            onSubmit={(values) => {
                const trimmed = values.ssn.trim();
                if (isSSN(trimmed)) {
                    tryPurchaseItem(trimmed, 'confirm-modal-with-reference')
                } else {
                    handleSearch(trimmed)
                }
            }}
            component={(props: { values: ISubFormValues }) => (
                <FormSubComponent
                    values={props.values}
                    disabled={(type === 'disabled')}
                    loading={isItemLoading(props.values.ssn) || searchButtonloading}
                />
            )}
        />
    )

    return (
        <div className='main KCL_thjodskra-search'>
            <div className='main__inner-fluid'>
                <div className="shell">
                    <HeadSection
                        noSpaceBetween={!isAuthenticated}
                        title={
                            <>
                                <h1>Þjóðskrá</h1>
                                {(isAuthenticated) && <HistoryButtonDropDown />}
                            </>
                        }
                        description={
                            <>
                                <p>Hér getur þú flett upp nöfnum og kennitölum í Þjóðskrá Íslands. </p>
                                <span>
                                    <strong>Athugið að ef leitað er eftir gildri kennitölu er um kaup að ræða. </strong>
                                    <Link linkSize='18'  url='/Verdskra#verdskra'>Sjá verðskrá.</Link>
                                </span>
                            </>
                        }
                    />
                    <div>
                        {(isAuthenticated)
                            ? displayForm('enabled')
                            : (
                                <Tooltip
                                    tooltip={
                                        <span>
                                            Þú þarft að skrá þig inn og vera með skráð kreditkort til að nýta þessa þjónustu.
                                        </span>
                                    }
                                    text={displayForm('disabled')}
                                />
                            )
                        }
                    </div>
                </div>
                {(searchTermResultName) &&
                    <div ref={searchResultRef} className="section--gray">
                        <div className='shell'>
                            <div className='title-wrapper'>
                                <h2>Leitarniðurstöður fyrir {searchTermResultName}</h2>
                                <FontAwesomeIcon onClick={() => setSearchTermResultName('')} icon={faXmark} className="icon-fa"/>
                            </div>
                            <DisplayBox>
                                {(searchResults === undefined)
                                    ? <Loading />
                                    : (searchResults.length === 0)
                                    ? <p>Engar niðurstöður fundust fyrir leitarorð: {searchTermResultName}</p>
                                    : <Table
                                        tableSize='lg'
                                        data={searchResults}
                                        columns={[{
                                            title: 'Nafn',
                                            dataAccessor: ({ name }) => name,
                                            textAlign: 'left',
                                            simpleSortable: ({ name }) => name,
                                        }, {
                                            title: 'Heimili',
                                            dataAccessor: ({ address }) => address,
                                            textAlign: 'left',
                                            simpleSortable: ({ address }) => address
                                        }, {
                                            title: '',
                                            dataAccessor: ({ ssn }) => (
                                                <Button
                                                    showLoader={isItemLoading(ssn)}
                                                    onClick={() => tryPurchaseItem(ssn, 'confirm-modal-with-reference')}
                                                    size='sm'
                                                    buttonType='buy'
                                                >
                                                    Kaupa
                                                </Button>
                                            ),
                                        }]}
                                    />
                                }
                            </DisplayBox>
                        </div>
                    </div>
                }
            </div>
            <ModalsAndToasts />
        </div>
    );
}

export default Thjodskra;
interface ISubFormValues {
    ssn: string
}

interface ISubFormProps {
    values: ISubFormValues
    loading: boolean
    disabled: boolean
}


const FormSubComponent: React.FC<ISubFormProps> = ({
    values,
    disabled,
    loading
}) => (
    <Form>
        <div className="form__body">
            <div className="form__section">
                <div className="individualsearchInputWrapper">
                    <div className="searchInput">
                        <Input
                            label='Leit í þjóðskrá'
                            disabled={disabled}
                            name="ssn"
                            id="ssn"
                            value={values.ssn}
                        />
                    </div>
                    <div className="searchButton">
                        {isSSN(values.ssn)
                        ? (
                            <Button
                                buttonType='buy'
                                showLoader={loading}
                                disabled={(disabled || values.ssn.length === 0)}
                                size="lg"
                            >
                                Kaupa
                            </Button>
                        ): (
                            <Button
                                showLoader={loading}
                                disabled={(disabled || values.ssn.length === 0)}
                                size="lg"
                            >
                                Leita
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </Form>
)